import React, { useCallback } from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import isObject from 'lodash/isObject';

// Utils
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';
import { handleLaunchExternalLink } from 'Shared/utils/external-link';
import { getCurrentMoment, convertMomentToUnix } from 'Shared/utils/DateTime.utils';

// Hooks
import { useUserFlag } from 'Shared/utils/hooks/use-user-flag';
import { useWorkspaceAgendaAi } from 'Shared/utils/hooks/use-workspace-agenda-ai';
import { useBannerTracking } from 'Shared/tracking/use-banner-tracking';
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';
import { useApp } from 'Shared/utils/hooks/use-app';

// Actions
import * as UsersActions from 'Shared/actions/Users.actions';

// Components
import { WpAnnouncementContainer } from 'Wpds/components/announcement-container';
import { WpButton } from 'Wpds/components/button';
import OAuthButton from 'Shared/components/OAuthButton/OAuthButton';

// Styles
import './AgendaAiAnnoucement.scss';

const AgendaAiAnnoucement = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { trackPrimaryCTAClick: trackSelectChannelsCTA, trackDismissClick: trackSelectDismis } = useBannerTracking({
    banner: 'Select Channels AgendaAI Announcement',
  });
  const { trackPrimaryCTAClick, trackDismissClick, trackLearnMoreClick } = useBannerTracking({
    banner: 'Connect Slack AgendaAI Announcement',
  });

  const { featureFlagAgendaAi, featureFlagShowAgendaAiAnnouncement: ffFlag, featureFlagEnablePiSuggested } = useFlags();
  const { numOfActiveChannels, slackAiOrganizationDataSourceId } = useWorkspaceAgendaAi({
    fetchAvailableChannels: false,
  });

  const { id: workspaceId } = useWorkspace();
  const { appId, isTeamsEmbeddedTab } = useApp();
  const isZoom = appId === 'zoom';

  const hasSeenConnectFlag = useUserFlag({ flag: 'dismissed_connect_slack_agenda_ai_announcement' });
  const hasSeenUseFlagValues = useUserFlag({ flag: 'dismissed_select_slack_agenda_ai_announcement' });

  const hasSeenUseFlag = isObject(hasSeenUseFlagValues) && Object.keys(hasSeenUseFlagValues).indexOf(workspaceId) >= 0;

  const showConnectMessage = !hasSeenConnectFlag && !slackAiOrganizationDataSourceId;
  const showUseageMessage = !hasSeenUseFlag && slackAiOrganizationDataSourceId && numOfActiveChannels === 0;

  const OAuthRedictUrl = `${PageUrlsUtil.makeWorkspaceSettingsUrl(workspaceId)}?show=agenda_ai`;

  const handleSelectChannelsClick = useCallback(() => {
    trackSelectChannelsCTA();
    history.push({
      pathname: PageUrlsUtil.makeWorkspaceSettingsUrl(workspaceId),
      search: '?show=agenda_ai',
    });
  }, []);

  const getTimeStamp = () => convertMomentToUnix(getCurrentMoment());

  const handleDismissConnectMessage = () => {
    trackDismissClick();
    dispatch(UsersActions.updateUserFlag('dismissed_connect_slack_agenda_ai_announcement', getTimeStamp()));
  };

  const handleDismissUseageMessage = () => {
    trackSelectDismis();

    const existingTracked = isObject(hasSeenUseFlagValues) ? hasSeenUseFlagValues : {};
    dispatch(
      UsersActions.updateUserFlag('dismissed_select_slack_agenda_ai_announcement', {
        ...existingTracked,
        [workspaceId]: getTimeStamp(),
      })
    );
  };

  const handleLearnMoreLink = () => {
    trackLearnMoreClick();
    handleLaunchExternalLink({ href: 'https://www.predictiveindex.com/software/perform/' });
  };

  if (featureFlagEnablePiSuggested) return null;
  if (isZoom) return null;
  if (isTeamsEmbeddedTab) return null;
  if (!ffFlag || !featureFlagAgendaAi) return null;
  if (!(showConnectMessage || showUseageMessage)) return null;

  const baseClassName = 'AgendaAiAnnoucement';

  return (
    <div className={baseClassName}>
      {showConnectMessage && (
        <WpAnnouncementContainer
          title="Introducing AgendaAI"
          text="Get suggested topics based off of your Slack discussions"
          onDismiss={handleDismissConnectMessage}
          button={
            <>
              <WpButton variant="tertiary" onClick={handleLearnMoreLink}>
                Learn more
              </WpButton>
              <OAuthButton
                oauthProvider="slack"
                text="Connect Slack"
                variant="secondary"
                color="primary"
                block={false}
                size="md"
                iconType="icon"
                redirectUrl={OAuthRedictUrl}
                onClick={trackPrimaryCTAClick}
              />
            </>
          }
          color="purple"
          image="https://piprodperformassets.blob.core.windows.net/assets/slack_announcement.png"
        />
      )}
      {showUseageMessage && (
        <WpAnnouncementContainer
          title="Use AgendaAI in this workspace"
          text="Connect relevant Slack channels to get suggested topics in this workspace"
          onDismiss={handleDismissUseageMessage}
          button={
            <WpButton variant="secondary" onClick={handleSelectChannelsClick}>
              Choose Slack Channels
            </WpButton>
          }
          color="gray"
          image="https://piprodperformassets.blob.core.windows.net/assets/slack_announcement.png"
        />
      )}
    </div>
  );
};

export default AgendaAiAnnoucement;
